/**
 * @file WmsExpiringPage.tsx
 * @description «Срок годности» — партии с близким сроком (phase13.0;
 * phase19.31 зона «Склад» — переведён целиком на Commora).
 *
 * Заголовок в верхней панели, главного действия нет. Фильтры — пилюли
 * «Все / До 7 дней / До 30 дней» со счётчиками; красный тон у «До 7 дней»
 * только при числе больше нуля (SC45). Список — `ResponsiveList`: срок —
 * статусом (скоро истекает — янтарный, истёк — красный). «Списать» — пункт
 * «⋯» строки: открывает новое списание с этой партией.
 *
 * `?wh=` — вход с плитки «Срок» обзора склада: склад, выбранный на сводке,
 * доезжает до списка.
 */

import { AlertTriangle, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterBar } from "../../../components/ui/FilterBar";
import { FilterChips } from "../../../components/ui/FilterChips";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { EXPIRING_COLUMNS } from "../../../components/wms/expiring/expiring-columns";
import { lotsApi, type LotDto } from "../../../lib/api-wms";
import { EXPIRY_CRITICAL_DAYS, EXPIRY_WARNING_DAYS, daysLeft } from "../../../lib/wms-expiry";
import { wmsErrorText } from "../../../lib/wms-error-text";

type Bucket = "all" | "critical" | "warning";

/** Порог окна: «все» — без отбора. */
const BUCKET_DAYS: Record<Exclude<Bucket, "all">, number> = {
  critical: EXPIRY_CRITICAL_DAYS,
  warning: EXPIRY_WARNING_DAYS,
};

function filterBucket(items: LotDto[], bucket: Bucket): LotDto[] {
  // Без срока партия в этот список не попадает вовсе.
  const dated = items.filter((lot) => lot.expiresAt);
  if (bucket === "all") return dated;
  return dated.filter((lot) => daysLeft(lot.expiresAt ?? "") <= BUCKET_DAYS[bucket]);
}

export function WmsExpiringPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const warehouseId = searchParams.get("wh") ?? "";
  const [items, setItems] = useState<LotDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<Bucket>("all");
  // Счётчик попыток: «Повторить» перезапускает загрузку.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    lotsApi
      .expiring({ alertDays: EXPIRY_WARNING_DAYS, warehouseId: warehouseId || undefined })
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить партии. Проверьте связь и попробуйте ещё раз."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, attempt]);

  const visible = useMemo(() => filterBucket(items, bucket), [items, bucket]);
  const counts = useMemo(
    () => ({ all: filterBucket(items, "all").length, critical: filterBucket(items, "critical").length, warning: filterBucket(items, "warning").length }),
    [items],
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Срок годности"
        subtitle="Партии, у которых скоро истекает срок. «Списать» открывает новое списание с этой партией"
      />

      {/* Телефон: окно срока — в шторке «Фильтры · N» (единое решение склада,
          phase19.31: «До 30 дней» обрезалось затуханием на 360). */}
      <FilterBar
        chipsCollapse={{ activeCount: bucket === "all" ? 0 : 1, onReset: () => setBucket("all") }}
        chips={
          <FilterChips
            aria-label="Окно срока годности"
            items={[
              { value: "all", label: "Все", count: counts.all },
              { value: "critical", label: `До ${EXPIRY_CRITICAL_DAYS} дней`, count: counts.critical, tone: "danger" },
              { value: "warning", label: `До ${EXPIRY_WARNING_DAYS} дней`, count: counts.warning },
            ]}
            value={bucket}
            onChange={setBucket}
          />
        }
      />

      <ResponsiveList
        aria-label="Партии с истекающим сроком"
        rows={visible}
        columns={EXPIRING_COLUMNS}
        rowKey={(lot) => lot.id}
        rowActions={(lot) => [
          {
            key: "writeoff",
            label: "Списать",
            icon: Trash2,
            run: () => navigate(`/shop/${slug}/wms/writeoffs/new?lotId=${lot.id}`),
          },
        ]}
        rowActionsTitle={(lot) => `Действия с партией ${lot.batchNumber}`}
        rowSize="double"
        tableMinWidth="min-w-[900px]"
        loading={loading}
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        filtered={bucket !== "all"}
        empty={{
          icon: AlertTriangle,
          title: `Ничего не истекает в ближайшие ${EXPIRY_WARNING_DAYS} дней`,
          description: "Партии появляются здесь по мере приближения срока годности.",
        }}
        filteredEmpty={{
          icon: Search,
          title: "В этом окне партий нет",
          description: "Посмотрите другое окно срока.",
          onReset: () => setBucket("all"),
          resetLabel: "Показать все",
        }}
      />
    </div>
  );
}
